import logger from '@/lib/logging';
type PendingRequest = {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  timeout: number;
};

type SocketState = 'disconnected' | 'connecting' | 'connected';

const DEFAULT_TIMEOUT_MS = 30_000;

class McpClient {
  private socket: WebSocket | null = null;
  private state: SocketState = 'disconnected';
  private pending = new Map<string, PendingRequest>();
  private operations: string[] = [];
  private reconnectDelay = 1000;

  async ensureConnected(): Promise<void> {
    if (this.state === 'connected') {
      return;
    }

    if (this.state === 'connecting') {
      await new Promise<void>((resolve) => {
        const interval = setInterval(() => {
          if (this.state === 'connected') {
            clearInterval(interval);
            resolve();
          }
        }, 100);
      });
      return;
    }

    this.state = 'connecting';

    return new Promise((resolve, reject) => {
      let settled = false;

      try {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const ws = new WebSocket(`${protocol}//${window.location.host}/mcp`);
        this.socket = ws;

        ws.onopen = () => {
          settled = true;
          this.state = 'connected';
          this.reconnectDelay = 1000;
          resolve();
        };

        ws.onmessage = (event) => {
          this.handleMessage(event);
        };

        ws.onerror = (event) => {
          logger.error('[MCP] WebSocket error', event);
          if (!settled) {
            settled = true;
            this.state = 'disconnected';
            reject(new Error('MCP WebSocket connection failed'));
          }
        };

        ws.onclose = () => {
          this.state = 'disconnected';
          this.rejectAllPending(new Error('MCP connection closed'));
          if (!settled) {
            settled = true;
            reject(new Error('MCP WebSocket closed before establishing connection'));
          } else {
            setTimeout(() => this.ensureConnected().catch(() => undefined), this.reconnectDelay);
            this.reconnectDelay = Math.min(this.reconnectDelay * 2, 10_000);
          }
        };
      } catch (error) {
        this.state = 'disconnected';
        reject(error);
      }
    });
  }

  private handleMessage(event: MessageEvent) {
    try {
      const payload = JSON.parse(event.data);

      switch (payload.type) {
        case 'welcome':
          this.operations = (payload.operations || []).map((op: any) => op.name);
          break;
        case 'chatResponse':
        case 'operationResult':
          if (payload.requestId && this.pending.has(payload.requestId)) {
            const entry = this.pending.get(payload.requestId)!;
            clearTimeout(entry.timeout);
            this.pending.delete(payload.requestId);
            entry.resolve(payload.response || payload.result);
          }
          break;
        case 'error':
          if (payload.requestId && this.pending.has(payload.requestId)) {
            const entry = this.pending.get(payload.requestId)!;
            clearTimeout(entry.timeout);
            this.pending.delete(payload.requestId);
            entry.reject(new Error(payload.message || payload.error || 'MCP error'));
          }
          break;
        default:
          break;
      }
    } catch (error) {
      logger.error('[MCP] Failed to parse message', error);
    }
  }

  private rejectAllPending(reason: Error) {
    this.pending.forEach((entry) => {
      clearTimeout(entry.timeout);
      entry.reject(reason);
    });
    this.pending.clear();
  }

  private send<T = any>(payload: Record<string, any>): Promise<T> {
    return new Promise(async (resolve, reject) => {
      try {
        await this.ensureConnected();
      } catch (error) {
        reject(error);
        return;
      }

      if (!this.socket || this.state !== 'connected') {
        reject(new Error('MCP connection unavailable'));
        return;
      }

      const requestId = crypto.randomUUID();
      const timeout = window.setTimeout(() => {
        if (this.pending.has(requestId)) {
          this.pending.get(requestId)?.reject(new Error('MCP request timed out'));
          this.pending.delete(requestId);
        }
      }, DEFAULT_TIMEOUT_MS);

      this.pending.set(requestId, { resolve, reject, timeout });
      this.socket.send(JSON.stringify({ ...payload, requestId }));
    });
  }

  async sendChat<T = any>(params: AgentChatRequestPayload): Promise<T> {
    return this.send<T>({
      type: 'chat',
      conversationId: params.conversationId,
      clientId: params.clientId,
      message: params.message,
      attachments: params.attachments,
      context: params.context,
    });
  }

  async executeOperation(name: string, args: Record<string, any>) {
    return this.send({
      type: 'operation',
      name,
      args,
    });
  }

  getOperations(): string[] {
    return this.operations;
  }
}

export interface AgentChatRequestPayload {
  conversationId?: string;
  message: string;
  clientId?: string;
  attachments?: any[];
  context?: {
    includeAssessments?: boolean;
    includePostureAnalyses?: boolean;
    includePrograms?: boolean;
  };
}

export const mcpClient = new McpClient();

